"use client";

// Zentraler ALTCHA-Spamschutz fuer die geteilte Anmeldehuelle (Anmelden,
// Registrieren, Passwort zuruecksetzen — ALLER Apps).
//
// **Es gibt keinen Empfaenger.** Die Aufgabe kommt vom eigenen Auth-Dienst,
// gerechnet wird mit `crypto.subtle` des Browsers — dabei faellt keine externe
// Anfrage an.
//
// **Unsichtbar.** Proof-of-Work verlangt vom Menschen gar nichts; er ist damit
// auch die barrierefreie Variante (WCAG 2.2 AA), waehrend ein Raetsel-Captcha
// eine der klassischen Barrieren ist.
//
// **Der Nachweis wird VORAB geholt, nicht beim Absenden.** Das Rechnen dauert
// rund eine Sekunde. Waehrend der Nutzer E-Mail und Passwort tippt, ist es
// laengst erledigt — beim Klick liegt der Nachweis bereit.
//
// **Fail-open liegt beim AUFRUFER, nicht hier**, wie beim Vorgaenger: diese
// Komponente meldet `null` mit einem Grund; ob daraus „durchlassen und melden"
// oder „abweisen" wird, entscheidet `spamschutzBestanden` in AuthOnboarding.
// Vorher trafen alle drei Einbindungen diese Entscheidung nicht und sperrten
// Nutzer aus, denen der Anbieter keinen Token gab (bis 0.181.0).

import { useEffect, useRef, useState } from "react";
import { getPublicEnv } from "@janeway/app-sdk/public-env";
import { ALTCHA_PFAD, holeAltchaNachweis, type AltchaHolFehler } from "@janeway/app-sdk/security/altcha-loeser";

/** Was am Ende der Rechnung herauskommt — Nachweis oder benannter Grund. */
export interface AltchaErgebnis {
  nachweis: string | null;
  fehler?: AltchaHolFehler;
}

/** Auth-Base-URL (generisch je Env) fuer den zentralen Spamschutz-Endpunkt. */
export function authBaseUrl(): string {
  return (getPublicEnv("NEXT_PUBLIC_AUTH_URL") ?? "").replace(/\/+$/, "");
}

/**
 * Loest einen Nachweis serverseitig ein. Nur ein hartes 403 blockt; alles andere
 * (kein Endpunkt, Netzfehler) laesst durch — dieselbe Abwaegung wie beim
 * Vorgaenger und aus demselben Grund: die Anmeldung selbst laeuft ueber GoTrue
 * und haengt sonst nicht an diesem Dienst. Waere sie hart, sperrte ein Ausfall
 * von janeway-auth jeden Nutzer aus jeder App der Suite aus.
 */
export async function verifySuiteAltcha(nachweis: string): Promise<boolean> {
  const base = authBaseUrl();
  if (!base) return true;
  try {
    const res = await fetch(`${base}${ALTCHA_PFAD}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ nachweis }),
    });
    return res.status !== 403;
  } catch {
    return true;
  }
}

/**
 * Meldet dem Server, dass gar nicht geprueft wurde. Fire-and-forget.
 *
 * **Warum ueberhaupt melden:** ohne diese Zeile ist ein uebersprungener
 * Spamschutz im Log nicht von einer bestandenen Pruefung zu unterscheiden —
 * beide hinterlassen nichts. Am 2026-08-19 liess sich an einem konkreten
 * Anmeldeversuch deshalb nicht mehr sagen, ob geprueft wurde. Fail-open ist eine
 * bewusste Entscheidung; sie unsichtbar zu treffen ist keine
 * (Art. 32 Abs. 1 lit. d DSGVO).
 */
export function meldeSpamschutzUebersprungen(grund: string): void {
  const base = authBaseUrl();
  if (!base) return;
  void fetch(`${base}${ALTCHA_PFAD}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ uebersprungen: true, grund }),
    keepalive: true,
  }).catch(() => {});
}

/** Ab wann dem Nutzer gesagt wird, dass gerade etwas laeuft. */
const HINWEIS_AB_MS = 2500;

export function LoginAltcha({
  onLauf,
  zweck = "anmeldung",
  resetSignal,
}: {
  /**
   * Der LAUFENDE Vorgang, nicht sein Ergebnis.
   *
   * **Warum ein Versprechen und kein fertiger Wert:** der Aufrufer sendet ab,
   * sobald der Nutzer klickt — und das kann VOR dem Ende der Rechnung sein. Ein
   * Zustandsfeld waere dann schlicht leer, der Aufrufer haette „kein Nachweis"
   * gelesen und durchgelassen. Das Gate liesse sich damit umgehen, indem man
   * schnell genug klickt; bei ~0,2 s Rechenzeit reicht dafuer ein Skript, das
   * das Formular gar nicht erst anzeigt. Mit dem Versprechen WARTET der
   * Aufrufer, statt ins Leere zu greifen.
   */
  onLauf: (lauf: Promise<AltchaErgebnis>) => void;
  zweck?: "anmeldung" | "registrierung";
  /** Hochzaehlen => neuen Nachweis holen. Ein Nachweis gilt genau einmal. */
  resetSignal: number;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const melde = useRef(onLauf);
  melde.current = onLauf;

  // Beim Aufbau und bei jedem Reset einen frischen Nachweis holen — sofort, damit
  // er fertig ist, bevor jemand tippt. Der Abbruch ist nicht Kosmetik: ohne ihn
  // rechnet eine verlassene Schleife weiter.
  useEffect(() => {
    const abbruch = new AbortController();
    const basis = authBaseUrl();

    // Der Hinweis erscheint NICHT sofort — sonst blitzt bei jedem Aufbau des
    // Formulars eine Zeile auf, die nach einer Sekunde wieder verschwindet und
    // das Layout zweimal springen laesst.
    const hinweis = window.setTimeout(() => setLaeuft(true), HINWEIS_AB_MS);

    // Ohne Auth-Adresse gibt es keinen Spamschutz. Das ist ein Fehlerfall, kein
    // stiller Normalzustand — der Aufrufer erfaehrt den Grund und meldet ihn.
    const lauf: Promise<AltchaErgebnis> = basis
      ? holeAltchaNachweis(basis, zweck, { signal: abbruch.signal })
      : Promise.resolve({ nachweis: null, fehler: "netz" as const });

    void lauf.then(() => {
      window.clearTimeout(hinweis);
      setLaeuft(false);
    });
    melde.current(lauf);

    return () => {
      abbruch.abort();
      window.clearTimeout(hinweis);
    };
  }, [zweck, resetSignal]);

  // Normalfall: nichts zu sehen. Der Hinweis erscheint erst, wenn das Rechnen
  // laenger dauert als die Zeit, die jemand mit dem Ausfuellen verbringt — sonst
  // erklaerte er eine Wartezeit, die niemand bemerkt.
  if (!laeuft) return null;
  return (
    <p className="text-xs text-muted-foreground" role="status" aria-live="polite">
      Spamschutz wird geprüft …
    </p>
  );
}
